import React, { useState, useEffect } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";

import PhoneNumbersPreview from "../components/PhoneNumbersPreview";

import PhoneNumberService from "../utils/phone-number-services";
import { uniqueStringArray } from "../utils/helpers";

interface Props {
	next: (value: string | string[]) => void;
	phones: string[];
	setPhones: React.Dispatch<React.SetStateAction<string[]>>;
	error: string;
	setError: React.Dispatch<React.SetStateAction<string>>;
}

const Audiences = ({ next, phones, setPhones, error, setError }: Props) => {
	const [phone, setPhone] = useState("");
	const [errorPhones, setErrorPhones] = useState<string[]>([]);

	const { formatPhoneNumber, isPhone, extractPhone, checkNumberLength } =
		PhoneNumberService;

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (Boolean(error.length)) {
			setError("");
		}
		setPhone(event.target.value);
	};

	const validatePhoneNumber = (number: string) => {
		const trimmed = number.trim();
		const formatted = formatPhoneNumber(number, false);
		const isPhoneNumber = isPhone(formatted);
		const isInPhonesArray = phones.includes(formatted);
		const hasSpecialChars = /[~`!#$%^&*=[\]\\';,/{}|\\":<>?]/g.test(trimmed);
		const hasAlphabeticalChars = /^[A-Za-z]+$/.test(trimmed);

		if (hasSpecialChars || hasAlphabeticalChars) {
			setError("Unrecognized characters in phone numbers.");
			return false;
		} else if (isInPhonesArray) {
			setError(
				"The number: " + formatted + " is already in the phone numbers list.",
			);
			return false;
		} else if (!isPhoneNumber || checkNumberLength(trimmed)) {
			setError(
				"Invalid phone number format. Please check the message below for a list of invalid numbers.",
			);
			return false;
		} else if (isPhoneNumber && !isInPhonesArray) {
			return true;
		}
	};

	const validateAndAddPhonesToArray = (nums: string[]) => {
		for (let i = 0; i < nums.length; i++) {
			const number = nums[i];
			if (validatePhoneNumber(number)) {
				const formatted = formatPhoneNumber(number, false);
				setPhones((prevPhones) =>
					uniqueStringArray([...prevPhones, formatted]),
				);
			} else {
				setErrorPhones((prevErrorPhones) =>
					uniqueStringArray([...prevErrorPhones, number]),
				);
			}
		}
	};

	const registerPhone = (nums: string[]) => {
		if (!nums) {
			setError("Please enter a phone number.");
			return;
		}

		if (errorPhones.length) {
			setErrorPhones([]);
		}

		const remainingAllowedPhones = 1000 - phones.length;

		if (nums.length > 1000) {
			setError("Max recipient is 1000");
		} else if (Boolean(phones) && remainingAllowedPhones < nums.length) {
			setError(
				`Max recipient is 1000, you can enter ${remainingAllowedPhones} more`,
			);
		} else {
			validateAndAddPhonesToArray(nums);
		}

		setPhone("");
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (["Enter", "Tab"].includes(event.key)) {
			event.preventDefault();
			const numbers = phone.includes(",")
				? phone.split(",")
				: extractPhone(phone);
			registerPhone(numbers);
		}
	};

	const handleAddClick = () => {
		const numbers = phone.includes(",")
			? phone.split(",")
			: extractPhone(phone);
		registerPhone(numbers);
	};

	const handleDelete = (phoneToDelete: string) => {
		setPhones((prevPhones) =>
			prevPhones.filter((phone) => phone !== phoneToDelete),
		);
	};

	const handleClearAll = () => {
		setPhones([]);
	};

	useEffect(() => {
		if (!Boolean(phones.length)) {
			setErrorPhones([]);
			setError("");
		}
	}, [phones]);

	return (
		<Box sx={{ p: 1.5 }}>
			<TextField
				type='tel'
				error={Boolean(error.length)}
				helperText={error}
				value={phone}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				size='small'
				placeholder='e.g: +1(888) 888-8888, (866) 888-8888, 8669999999, (888)8999999'
				sx={{ mr: 1, width: "80%" }}
			/>
			<Button
				variant='contained'
				startIcon={<AddIcon />}
				onClick={handleAddClick}>
				Add
			</Button>
			{Boolean(errorPhones.length) && (
				<Alert severity='error' sx={{ mt: 2 }}>
					Invalid phone numbers: <br /> <br />
					{errorPhones.map((phone) => (
						<li key={phone}>
							<b>{phone}</b>
						</li>
					))}
				</Alert>
			)}
			{Boolean(phones.length) ? (
				<>
					<PhoneNumbersPreview
						phones={phones}
						handleClearAll={handleClearAll}
						handleDelete={handleDelete}
					/>

					<Box sx={{ mt: 2 }}>
						<Button variant='contained' onClick={() => next(phones)}>
							Next
						</Button>
					</Box>
				</>
			) : null}
		</Box>
	);
};

export default Audiences;
